<script lang="ts">
	import { mutation } from "@api/graphql.js";
	import sendWebMessageMutation from "@api/mutations/sendWebMessage.graphql";
	import Alert from "@components/Alert.svelte";
	import RecaptchaDisclaimer from "@components/RecaptchaDisclaimer.svelte";
	import Button from "@components/common/Button.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Modal from "@components/common/Modal.svelte";
	import Form from "@components/form/Form.svelte";
	import Input from "@components/form/Input.svelte";
	import Textarea from "@components/form/Textarea.svelte";
	import { pushGenerateLeadEvent } from "@utils/pushGenerateLeadEvent.js";
	import { type Snippet, untrack } from "svelte";
	import linkedin from "@core/assets/icons/linkedin.svg?raw";
	import youtube from "@core/assets/icons/youtube.svg?raw";
	import fastestLogo from "@core/assets/images/fastest-logo.svg?raw";
	import commarecLogo from "@core/assets/logo.svg?raw";
	import { ButtonVariant } from "@core/schema/ButtonVariant.js";
	import type { WebMessageInput } from "@core/schema/WebMessage.js";
	import { WebMessageType } from "@core/schema/WebMessageType.js";
	import { getContactFormAnchor } from "@core/schema/paths/getContactFormAnchor.js";
	import { getGdprPath } from "@core/schema/paths/getGdprPath.js";
	import { getHomepagePath } from "@core/schema/paths/getHomepagePath.js";
	import { cleanPhoneNumber } from "@core/utils/cleanPhoneNumber.js";
	import { formatPhoneNumber } from "@core/utils/formatPhoneNumber.js";
	import { formatPhoneNumberOnInput } from "@core/utils/svelte/formatPhoneNumberOnInput.js";
	import { companyPhone } from "../../contacts/companyPhone.js";
	import { getGlobalSettings } from "../../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import LabelledInput from "../form/LabelledInput.svelte";

	const contacts: Record<string, { type: "tel" | "email"; value: string }> = {
		Telefon: { type: "tel", value: companyPhone },
		"E-mail": { type: "email", value: "info@commarec.cz" },
		Faktury: { type: "email", value: "fakturace@commarec.cz" },
		Vedení: { type: "email", value: "executive@commarec.cz" },
	};
	const { tenant } = getGlobalTemplateProps();
	const { PUBLIC_GRAPHQL_ENDPOINT } = getGlobalSettings();
	const { stickyBottom }: { stickyBottom?: Snippet } = $props();

	const social: Record<string, [icon: string, href: string]> = {
		LinkedIn: [linkedin, "https://www.linkedin.com/company/commarec/"],
		YouTube: [youtube, "https://www.youtube.com/@commareclogistika"],
	};

	let loading = $state(false);
	let showModal = $state(false);
	let modalMessage = $state("");
	let isError = $state(false);
	let phoneValue = $state("");

	async function submitContactForm(event: SubmitEvent, form: HTMLFormElement): Promise<void> {
		event.preventDefault();
		const formData = new FormData(form);
		loading = true;
		try {
			if (!navigator.onLine) {
				throw new Error("Nejste připojeni k internetu.");
			}
			const now = new Date().toISOString();
			await mutation<boolean, { input: WebMessageInput }>(PUBLIC_GRAPHQL_ENDPOINT, sendWebMessageMutation, {
				input: {
					gdprApprovedAt: now,
					name: formData.get("name") as string,
					phoneNumber: cleanPhoneNumber(phoneValue),
					email: formData.get("email") as string,
					message: formData.get("message") as string,
					locale: tenant.locale,
					sentFromUrl: location.href,
					type: WebMessageType.Contact,
				},
			});
			isError = false;
			pushGenerateLeadEvent("contact");
			modalMessage = "Vaše zpráva byla úspěšně odeslána. Podrobnosti jsme Vám zaslali na Vaši e-mailovou adresu.";
			showModal = true;
			untrack(() => {
				phoneValue = "";
			});
			form.reset();
		} catch (err) {
			isError = true;
			modalMessage = `Stala se chyba. Zkuste, prosím, formulář odeslat později. Detail chyby: ${err instanceof Error ? err.message : String(err)}.`;
			showModal = true;
		} finally {
			loading = false;
		}
	}

	let height = $state(0);

	const inputBorder = "!border-[#5D7395] !border-[1.5px]";
</script>

<footer
	class="bg-cmcnavy overflow-hidden p-4 md:p-8 text-white flex flex-col gap-16 *:max-w-[156rem] *:w-full items-center"
>
	<div class="grid grid-cols-2 md:grid-cols-4 gap-x-1 gap-y-8 md:gap-8 text-left">
		<a href={getHomepagePath(tenant)} aria-label="Commarec">
			<Icon icon={commarecLogo} class="h-8 aspect-[4.5]" />
		</a>

		<div class="flex justify-end gap-4 md:order-4 min-[3000px]:justify-start">
			{#each Object.entries(social) as [title, [icon, href]] (title)}
				<a
					class="border-[0.09375rem] border-cmcprimary rounded-full size-6 md:size-12 flex items-center justify-center"
					{href}
					{title}
					aria-label={title}
					target="_blank"
					rel="noopener noreferrer"
				>
					<Icon class="text-cmcprimary size-4 md:size-8" {icon} />
				</a>
			{/each}
		</div>

		<address class="flex flex-col gap-3 text-2xs/[1] md:text-xs/[1] not-italic md:order-2">
			<div class="font-bold">
				<Formatted text="Commarec s.r.o." />
			</div>
			<div><Formatted text="Varšavská 715/36" /></div>
			<div><Formatted text="120 00 Praha 2" /></div>
			<div><Formatted text="Česká republika" /></div>
		</address>

		<div
			class="grid text-2xs/[1] md:text-xs/[1] w-fit gap-x-2 grid-cols-[auto,auto] justify-end md:justify-normal md:order-3"
			role="table"
		>
			{#each Object.entries(contacts) as [key, { value, type }] (key)}
				<div class="contents group" role="row">
					<div role="cell" class="py-1.5 group-first:pt-0 group-last:pb-0">{key}:</div>
					<div role="cell" class="contents">
						<a
							href={{ tel: "tel:", email: "mailto:" }[type] + value}
							class="font-bold py-1.5 group-first:pt-0 group-last:pb-0"
						>
							<Formatted text={type === "tel" ? formatPhoneNumber(value) : value} />
						</a>
					</div>
				</div>
			{/each}
		</div>
	</div>

	<div class="flex flex-col gap-8 xl:flex-row md:justify-between">
		<div class="flex text-center xl:text-left flex-col gap-6">
			<h2
				class="text-[#214E92] text-[4rem]/[0.9] sm:text-8xl xl:text-9xl/[0.9] font-black uppercase 2xl:text-10xl/[0.9] font-druk-cond"
			>
				<Formatted text="Napište nám" />
			</h2>
			<p class="text-white hidden md:inline-block font-bold text-xl/[1.2]">
				<Formatted text="Vaše cesta k efektivnější logistice začne nl[[]] odesláním tohoto formuláře" />
			</p>
			<p class="text-white font-bold md:hidden text-sm/[1.2] tracking-[0.01em]">
				<Formatted text="Vaše cesta k efektivnější logistice nl[[]] začne odesláním tohoto formuláře" />
			</p>
		</div>
		<Form
			id={getContactFormAnchor(tenant)}
			onsubmit={submitContactForm}
			class="flex flex-col gap-2 md:gap-4 max-xl:mx-auto w-full md:w-[40rem] lg:w-[50rem] xl:w-[40rem] 2xl:w-[42rem] min-[1800px]:2xl:w-[56rem]"
		>
			<Input
				required
				placeholder="Jméno"
				name="name"
				class={`h-7 md:h-auto text-[.625rem] md:text-base placeholder:text-[#AFAFAF] ${inputBorder}`}
			/>
			<Input
				required
				placeholder="Telefon"
				name="phone"
				type="tel"
				bind:value={phoneValue}
				oninput={formatPhoneNumberOnInput}
				pattern="[\d\s\+\-]+"
				maxlength={20}
				class={`h-7 md:h-auto ${inputBorder}`}
			/>
			<Input required placeholder="E-mail" name="email" type="email" class={`h-7 md:h-auto ${inputBorder}`} />
			<Textarea
				required
				placeholder="Stačí, když nám ve formuláři krátce popíšete, s čím potřebujete pomoci, a my se co nevidět ozveme."
				name="message"
				rows={4}
				class={inputBorder}
			/>
			<div class="flex justify-between xl:flex-row items-center gap-3 md:gap-4 xl:gap-0">
				<LabelledInput
					type="checkbox"
					name="gdprApprovedAt"
					required
					class="items-center flex"
					label={{ class: "text-white" }}
				>
					<Formatted text="Souhlasím se l[{getGdprPath(tenant)}][[zpracováním osobních údajů]]" />
				</LabelledInput>
				<Button
					icon={loading ? "loading" : "arrow"}
					disabled={loading}
					variant={ButtonVariant.Primary}
					class="inline-flex gap-4 w-fit self-end"
				>
					<Formatted text={loading ? "Odesílám" : "Odeslat"} />
				</Button>
			</div>

			<RecaptchaDisclaimer />
		</Form>
	</div>
	<div class="relative text-right flex flex-col gap-4 sm:block" style:margin-bottom="{height}px">
		<div
			class="ml-8 w-64 absolute bottom-0 left-1/2 -translate-x-1/2 xl:translate-x-0 xl:translate-y-full justify-center flex items-center gap-0 sm:gap-1.5 xl:left-0"
		>
			<div class="inline-flex items-center ml-2 sm:ml-0 sm:gap-2">
				<span class="text-xs sm:text-sm">Zkatalogizováno ve</span>
				<a href="https://www.fastest.cz" target="_blank" aria-label="Fastest">
					<Icon icon={fastestLogo} class="h-3 sm:h-4 w-fit mb-1 sm:mb-1 -ml-2 sm:ml-0" />
				</a>
			</div>
		</div>
		<div
			class="huge-heading font-druk-cond text-center xl:text-right mb-14 md:mb-16 xl:mb-4 hidden md:inline-block text-cmcsecondary stroke-text-white"
		>
			<Formatted text="Logistika není raketová věda, pokud víte, jak na ni." />
		</div>
	</div>
</footer>

{#if stickyBottom}
	<div bind:clientHeight={height} class="fixed bottom-0 z-40 inset-x-0">
		{@render stickyBottom()}
	</div>
{/if}

<Modal bind:show={showModal} class="w-[50rem] px-6 max-w-[calc(100vw-1rem)]">
	<div class="flex flex-col gap-8 w-full">
		<h3 class="text-[4rem] font-bold uppercase font-druk-cond sm:absolute top-6 text-cmcsecondary">
			{isError ? "Chyba" : "Úspěch"}
		</h3>
		<Alert type={isError ? "warning" : "success"} class="sm:mt-4">
			<Formatted text={modalMessage} />
		</Alert>
		<Button
			icon="none"
			variant={ButtonVariant.Primary}
			class="self-end"
			onclick={(): void => {
				showModal = false;
			}}
		>
			<Formatted text="OK" />
		</Button>
	</div>
</Modal>
